import React, { useState, useRef, useEffect, useMemo } from "react";
import { CloudAdd24Filled, CloudAdd24Regular, Delete24Filled, DocumentBulletList24Filled } from "@fluentui/react-icons";
import { useToast } from "../../ToastProvider";
import { FileReference } from "../OiReport";

import { useEventReport } from "../redux/ReduxContext";

export interface OiFilesData {
    attachedFiles: FileReference[];
}

interface OiFilesProps {
    onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

const OiFiles: React.FC<OiFilesProps> = ({
    onOpenFileGallery
}) => {

    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToast();
    const { state, dispatch } = useEventReport();

    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            await processFiles(files);
        }
    };

    const processFiles = async (files: FileList) => {
        if (!files || files.length === 0) return;
        setIsUploading(true);

        try {
            const { UploadFile } = await import("../../../services/OiService");

            const uploadPromises = Array.from(files).map(async (file) => {
                try {
                    const fileId = await UploadFile(file);
                    const cleanFileId = fileId ? fileId.toString().replace(/^["']|["']$/g, '') : null;
                    return cleanFileId ? { uuid: cleanFileId, fileName: file.name } : null;
                } catch (error) {
                    console.error(`Failed to upload ${file.name}:`, error);
                    showToast({ title: 'Upload failed', intent: 'error', body: `Failed to upload ${file.name}` });
                    return null;
                }
            });

            const uploadResults = await Promise.all(uploadPromises);

            if (uploadResults.length > 0) {

                uploadResults.forEach(upload => {
                    if (upload) {
                        dispatch({ type: 'ADD_ARRAY_ITEM', payload: { field: 'referencedDocuments', item: upload } });
                    }
                });

            }
        } catch (error) {
            showToast({ title: 'Upload failed', intent: 'error', body: `Failed to upload files` });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
    };

    const handleFileToggle = (fileUuidToRemove: string) => {
        let index = state.referencedDocuments.findIndex(file => file.uuid === fileUuidToRemove);
        if (index !== -1) {
            dispatch({ type: 'DELETE_ARRAY_ITEM', payload: { field: 'referencedDocuments', index } });
        }
    };

    const openGalleryAtFile = (fileUuid: string) => {
        let index = state.referencedDocuments.findIndex(file => file.uuid === fileUuid);
        onOpenFileGallery?.(state.referencedDocuments, index >= 0 ? index : 0);
    };

    return (
        <div
            className="relative p-4 bg-white dark:bg-oi-dark-background transition-all duration-200 drop-shadow-md"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <div className="flex items-center gap-2">
                <h6>Attachments</h6>
            </div>

            {isDragOver && (
                <div className="absolute inset-0 bg-blue-100/80 dark:bg-blue-900/40 flex items-center justify-center pointer-events-none rounded-md">
                    <div className="text-center">
                        <CloudAdd24Regular className="mx-auto mb-2 text-4xl text-blue-600" />
                        <p className="text-lg font-medium text-blue-700 dark:text-blue-300">
                            Drop files here to upload
                        </p>
                    </div>
                </div>
            )}

            <div className="mb-2 justify-items-end grid">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="*/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                />
            </div>

            {isUploading && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                    <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-blue-700 dark:text-blue-300">
                            Uploading files...
                        </span>
                    </div>
                </div>
            )}

            {state.referencedDocuments.length > 0 && (
                <div className="max-h-40 overflow-y-auto">
                    <div className="">
                        {state.referencedDocuments.map((fileItem, index) => (
                            <div
                                key={`new-${index}`}
                                className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded transition-colors"
                            >
                                <DocumentBulletList24Filled className="w-4 h-4" />
                                <button
                                    onClick={() => {
                                        try {
                                            openGalleryAtFile(fileItem.uuid);
                                        } catch (error) {
                                            console.error('Error opening file:', error);
                                            showToast({ title: 'Error opening file', intent: 'error', body: `Could not open file: ${fileItem.fileName}` });
                                        }
                                    }}
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline bg-transparent border-none cursor-pointer p-0 text-left"
                                    title={`Open ${fileItem.fileName}`}
                                >
                                    {fileItem.fileName}
                                </button>
                                <Delete24Filled className="text-bad-red w-4 h-4 cursor-pointer" onClick={() => handleFileToggle(fileItem.uuid)} />
                            </div>

                        ))}
                    </div>
                </div>
            )}

            {!isUploading && (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-primary-dark dark:border-primary-light rounded-lg my-6 cursor-pointer" onClick={handleFileSelect} >
                    <CloudAdd24Filled className="mx-auto mb-3 text-3xl opacity-50 w-12 h-12" />
                    <p className="text-xs">Click or drag and drop files here</p>
                </div>
            )}
        </div>
    );
}
export default OiFiles;